import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IcSearch,
  IcClose,
  IcUser,
  IcUserPlus,
  IcUsers,
  IcPencil,
  IcTrash,
  IcReceiptText,
} from '../../theme/icons';
import Swal from '../../theme/swal';
import authService from '../../services/authService';
import KI from '../../theme/palette';
import { filterSearch } from '../../utils/search';

// Étiquettes Ki Para §13 (comptes) : Admin amber · Vendeur gris discret.
const ROLE_STYLES = {
  admin: 'bdg-warn',
  manager: 'bdg-pos',
  seller: 'bdg-neutral',
};
const ROLE_LABELS = { admin: 'Propriétaire', manager: 'Manager', seller: 'Caisse' };

function RolePill({ role }) {
  const cls = ROLE_STYLES[role] || 'bdg-neutral';
  return (
    <span className={`bdg ${cls} py-0.5 rounded-full`}>
      {ROLE_LABELS[role] || role}
    </span>
  );
}

// ● Actif vert teinté · ○ Inactif gris (jamais rouge : inactif n'est pas un problème).
function StatusPill({ active }) {
  return (
    <span className={`bdg ${active ? 'bdg-pos' : 'bdg-neutral'} py-0.5 rounded-full`}>
      {active ? '● Actif' : '○ Inactif'}
    </span>
  );
}

function UserCard({ user, onEdit, onDelete, isSelf }) {
  const hasFacturation =
    user.role === 'admin' || user.role === 'manager' || user.has_facturation_access === true;

  return (
    <div className="flex items-center gap-3 p-3 bg-white rounded-2xl border border-gray-200">
      {/* Avatar de profil (Ahmed 2026-08-16) : silhouette humaine noir & blanc,
          comme la carte profil (MoreSheet). */}
      <div className="h-14 w-14 rounded-full bg-gray-100 text-gray-900 flex-shrink-0 flex items-center justify-center">
        <IcUser className="text-xl" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="font-medium text-gray-900 truncate">{user.username}</div>
        <div className="text-xs text-gray-500 truncate">{user.email}</div>
        <div className="mt-1 flex items-center gap-2 flex-wrap">
          <RolePill role={user.role} />
          <StatusPill active={user.is_active} />
          <span className="text-xs text-gray-500">{user.role === 'admin' ? 'Toutes les boutiques' : ((user.store_names || []).join(', ') || 'Sans boutique')}</span>
          {hasFacturation && (
            <span className="bdg bdg-pos py-0.5 rounded-full">
              <IcReceiptText className="text-[11px]" />
              Accès facturation
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2 flex-shrink-0">
        <button
          onClick={() => onEdit(user.id)}
          aria-label="Modifier"
          className="h-9 w-9 flex items-center justify-center rounded-xl bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors"
        >
          <IcPencil />
        </button>
        <button
          onClick={() => onDelete(user.id)}
          aria-label="Supprimer"
          disabled={isSelf}
          className="h-9 w-9 flex items-center justify-center rounded-xl text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <IcTrash />
        </button>
      </div>
    </div>
  );
}

export default function MobileUserList() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await authService.getAllUsers();
      if (Array.isArray(data)) {
        setUsers(data);
      } else if (data && data.results && Array.isArray(data.results)) {
        setUsers(data.results);
      } else if (data && typeof data === 'object') {
        const possibleUserArrays = Object.values(data).filter((val) => Array.isArray(val));
        setUsers(possibleUserArrays.length > 0 ? possibleUserArrays[0] : []);
      } else {
        setUsers([]);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Admin only — redirect everyone else, load users for admins.
  useEffect(() => {
    if (!authService.isOwner()) {
      window.location.href = '/';
    } else {
      fetchUsers();
    }
  }, [fetchUsers]);

  const handleEdit = (userId) => navigate(`/users/edit/${userId}`);

  const deleteUser = async (userId) => {
    try {
      const result = await Swal.fire({
        title: 'Êtes-vous sûr ?',
        text: 'Cette action est irréversible !',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: KI.coralDark,
        cancelButtonColor: KI.peacock,
        confirmButtonText: 'Oui, supprimer',
        cancelButtonText: 'Annuler',
      });

      if (result.isConfirmed) {
        await authService.deleteUser(userId);
        Swal.fire('Supprimé !', "L'utilisateur a été supprimé.", 'success');
        fetchUsers();
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      Swal.fire('Erreur !', "Un problème est survenu lors de la suppression.", 'error');
    }
  };

  // Règle Ki Para (utils/search.js) : nom / rôle par début de mot, e-mail « contient ».
  const filtered = filterSearch(users, search, { text: (u) => [u.username, u.role], codes: (u) => [u.email] });

  const currentUserId = authService.getCurrentUser()?.user_id;

  return (
    <div className="w-full px-4 py-6">
      {/* Nom de page = chip blanc bordure noire, comme MobileProductList
          (Studio 2026-08-14) ; ancien h1 blanc retiré. */}
      <div className="mb-4">
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold bg-white text-gray-900">
          <IcUsers size={13} /> Utilisateurs
        </span>
      </div>
      {/* Search */}
      <div className="relative mb-4">
        <IcSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher par nom, email ou rôle..."
          className="w-full pl-11 pr-10 py-2.5 border border-gray-200 rounded-xl bg-white focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <IcClose />
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
          <IcUsers className="mx-auto text-5xl text-gray-300 mb-4" />
          <p className="text-gray-500 font-medium">Aucun utilisateur trouvé</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {filtered.map((u) => (
            <UserCard
              key={u.id}
              user={u}
              onEdit={handleEdit}
              onDelete={deleteUser}
              isSelf={u.id === currentUserId}
            />
          ))}
        </div>
      )}

      {/* Add-user FAB */}
      <button
        onClick={() => navigate('/users/add')}
        aria-label="Ajouter un utilisateur"
        className="btn-p fixed right-4 bottom-24 z-30 h-14 w-14 rounded-full shadow-lg transition-colors"
      >
        <IcUserPlus className="text-xl" />
      </button>
    </div>
  );
}
